import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Company from "../api/Company";
import { toast } from "react-hot-toast";
import { emitCustomEvent } from "react-custom-events";
import Input from "../components/form/Input";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../components/organisms/Layout";
import { Loader2 } from "lucide-react";

const AddCompany = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);
  const [isEnterprise, setIsEnterprise] = useState(false);

  useEffect(() => {
    document.title = "HeyHire Portal-Add New Company";
  }, []);

  const addCompany = () => {
    setLoading(true);
    Company.add({ name: name, is_enterprise: isEnterprise ? 1 : 0 })
      .then(() => {
        toast.success(t("company-added"));
        emitCustomEvent("companyAdded");
        navigate("/companies");
      })
      .catch((error) => {
        if (error.response && error.response.status === 422) {
          setValidationErrors(error.response.data.errors);
        } else {
          if (error?.response?.status !== 403) {
            toast.error(t("common.something-went-wrong-please-try-again"));
          }
        }
      })
      .then(() => setLoading(false));
  };

  return (
    <DashboardLayout darmo={true}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900">
            {t("companies.add-a-company")}
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Fill in the information below to create a new company.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white shadow-sm ring-1 ring-gray-300 rounded-xl p-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              addCompany();
            }}
            className="space-y-8"
          >
            {/* Company Information Section */}
            <div>
              <h3 className="text-base font-semibold leading-7 text-gray-900">
                Company Information
              </h3>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                Basic details about the company.
              </p>

              <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                <div className="col-span-full sm:col-span-1">
                  <Input
                    darmo={true}
                    name="name"
                    label={t("common.name")}
                    type="text"
                    value={name}
                    placeholder="Enter company name"
                    error={
                      validationErrors && validationErrors.name
                        ? validationErrors.name[0]
                        : null
                    }
                    onChange={(e) => setName(e.target.value)}
                    className="w-full"
                  />
                </div>

                <div className="col-span-full sm:col-span-1">
                  <div className="flex items-center h-full pt-6">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={isEnterprise}
                        onChange={() => setIsEnterprise(!isEnterprise)}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      <span className="ml-3 text-sm font-medium text-gray-900">
                        Enterprise Company
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="border-t border-gray-900/10 pt-8 flex justify-end gap-x-3">
              <button
                type="button"
                onClick={() => navigate("/companies")}
                className="rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  t("common.add")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddCompany;
